import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Calendar, Check, Coins, Loader2, Send } from "lucide-react";
import { cn } from "@/lib/cn";
import { apiFetch } from "@/api/client";
import { useContractPriceTypes, useSubmitForApproval, useUpdateContractTerms } from "@/hooks/useContracts";
import type { ContractDetailResponse, ContractTermsUpdatePayload } from "@/api/types";
import { ContractTermsFields, FieldShell, inputCls } from "./ContractTermsFields";

// Contract UNIFICATION — editable draft surface (mockup v0.3 §CommercialTerms,
// §2 write-policy). Rendered IN-PLACE inside the same ContractDetailV2 detail when
// allowedActions.canEditDraft (crm_managed draft). Editable set per §2:
// payment (deferral/prepayment) · credit (+currency) · price-type (BI reference,
// required* before submit) · наша-юрособа · строки. NO contract amount (AC-CR-11).
// G1-S1: the commercial-fields half is the shared ContractTermsFields block,
// reused by the create-wizard "Комерційні умови" step.

interface LegalEntityOption { id: string; name: string }

export function DraftTermsEditor({ contract }: { contract: ContractDetailResponse }) {
  const t = contract.commercialTerms;

  const [deferralDays, setDeferralDays] = useState(t.deferralDays?.toString() ?? "");
  const [prepayment, setPrepayment] = useState(t.prepaymentPercent?.toString() ?? "");
  const [creditLimit, setCreditLimit] = useState(t.creditLimit?.toString() ?? "");
  const [currency, setCurrency] = useState(t.currency ?? "UAH");
  const [priceTypeId, setPriceTypeId] = useState(t.priceTypeId ?? "");
  const [legalEntityId, setLegalEntityId] = useState(contract.legalEntity?.id ?? "");
  const [effectiveDate, setEffectiveDate] = useState(contract.effectiveDate ?? "");
  const [expirationDate, setExpirationDate] = useState(contract.expirationDate ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const priceTypesQ = useContractPriceTypes();
  const updateMut = useUpdateContractTerms(contract.id);
  const submitMut = useSubmitForApproval(contract.id);

  const [legalEntities, setLegalEntities] = useState<LegalEntityOption[]>([]);
  useEffect(() => {
    apiFetch<{ list: Array<{ id: string; name: string }> }>("/KormaxLegalEntity", {
      query: { select: "id,name", maxSize: 100 },
    })
      .then((res) => setLegalEntities(res.list ?? []))
      .catch(() => setLegalEntities([]));
  }, []);

  // Dirty = any field diverges from the persisted contract (gates Submit on save).
  const dirty = useMemo(() => {
    const numEq = (s: string, n: number | null) =>
      s.trim() === "" ? n === null : Number(s) === n;
    return (
      !numEq(deferralDays, t.deferralDays) ||
      !numEq(prepayment, t.prepaymentPercent) ||
      !numEq(creditLimit, t.creditLimit) ||
      (currency || "UAH") !== (t.currency ?? "UAH") ||
      (priceTypeId || "") !== (t.priceTypeId ?? "") ||
      (legalEntityId || "") !== (contract.legalEntity?.id ?? "") ||
      (effectiveDate || "") !== (contract.effectiveDate ?? "") ||
      (expirationDate || "") !== (contract.expirationDate ?? "")
    );
  }, [deferralDays, prepayment, creditLimit, currency, priceTypeId, legalEntityId, effectiveDate, expirationDate, t, contract]);

  const num = (s: string): number | null => (s.trim() === "" ? null : Number(s));

  const save = async () => {
    setError(null);
    setSaved(false);
    if (!legalEntityId) { setError("Оберіть нашу юрособу."); return; }
    const payload: ContractTermsUpdatePayload = {
      deferralDays: num(deferralDays),
      prepaymentPercent: num(prepayment),
      creditLimit: num(creditLimit),
      currency: currency.trim() || null,
      priceTypeId: priceTypeId || null,
      legalEntityId,
      effectiveDate: effectiveDate || null,
      expirationDate: expirationDate || null,
    };
    try {
      await updateMut.mutateAsync(payload);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не вдалося зберегти умови.");
    }
  };

  const submit = async () => {
    setError(null);
    if (!priceTypeId) { setError("Тип цін обов'язковий перед поданням на погодження."); return; }
    if (dirty) { setError("Спершу збережіть зміни умов."); return; }
    try {
      await submitMut.mutateAsync();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не вдалося подати на погодження.");
    }
  };

  const busy = updateMut.isPending || submitMut.isPending;
  const canSubmit = contract.allowedActions.canSubmitForApproval;

  return (
    <div className="rounded-xl border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-2 text-sm font-medium">
        <span className="flex items-center gap-2"><Coins className="h-4 w-4 text-primary" />Умови (чернетка — редагується)</span>
        <span className="rounded px-1.5 py-0.5 text-[11px] font-medium bg-primary/10 text-primary">CRM draft</span>
      </div>

      <div className="space-y-4 p-4">
        {t.reviewState === "returned_for_revision" && (
          <div className="rounded-md bg-warning px-2 py-1 text-[11px] font-medium text-warning-foreground">
            повернуто на доопрацювання — внесіть правки та подайте знову
          </div>
        )}

        {/* Наша юрособа + строки */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FieldShell label="Наша юрособа (Kormax)" required>
            <select className={inputCls} value={legalEntityId} onChange={(e) => setLegalEntityId(e.target.value)}>
              <option value="">Оберіть юрособу</option>
              {legalEntities.map((le) => <option key={le.id} value={le.id}>{le.name}</option>)}
            </select>
          </FieldShell>
          <div className="grid grid-cols-2 gap-2">
            <FieldShell label="Початок дії">
              <div className="flex items-center gap-1.5 rounded-md border px-2 h-9">
                <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} className="w-full bg-transparent text-sm outline-none" />
              </div>
            </FieldShell>
            <FieldShell label="Діє до">
              <div className="flex items-center gap-1.5 rounded-md border px-2 h-9">
                <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <input type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} className="w-full bg-transparent text-sm outline-none" />
              </div>
            </FieldShell>
          </div>
        </div>

        {/* Commercial terms — shared canonical block (G1-S1) */}
        <ContractTermsFields
          value={{ deferralDays, prepayment, creditLimit, currency, priceTypeId }}
          onChange={(v) => {
            setDeferralDays(v.deferralDays);
            setPrepayment(v.prepayment);
            setCreditLimit(v.creditLimit);
            setCurrency(v.currency);
            setPriceTypeId(v.priceTypeId);
          }}
          priceTypes={priceTypesQ.data?.items ?? []}
          priceTypesLoading={priceTypesQ.isPending}
        />

        {error && (
          <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5" />{error}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          <button
            type="button"
            onClick={save}
            disabled={busy || !dirty}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium",
              dirty ? "bg-primary text-primary-foreground" : "border text-muted-foreground",
            )}
          >
            {updateMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Зберегти умови
          </button>
          {saved && !dirty && <span className="text-xs text-primary">збережено</span>}
          {canSubmit && (
            <button
              type="button"
              onClick={submit}
              disabled={busy || dirty || !priceTypeId}
              title={!priceTypeId ? "Спершу оберіть тип цін" : dirty ? "Спершу збережіть умови" : undefined}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Подати на погодження
            </button>
          )}
        </div>
        {!canSubmit && (
          <p className="text-[11px] text-muted-foreground">
            Для подання на погодження завантажте основний документ договору.
          </p>
        )}
      </div>
    </div>
  );
}
